import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { useHistory } from 'react-router-dom';
import Page from '@/model/Page';
import { ProducerProductLink } from '@/model/ProducerProductLinkDTO';
import { States } from '@/helpers/common/states';
import api from '@/services/api';
import { useDialog } from '@/hooks/useDialog';
import useForm from '@/hooks/useForm';
import Event from '@/model/Event';
import Section from '@/model/Section';
import { FormInputName, ShouldShowModal } from '../../types';
import { ProducerProductContainer } from './ui';

export const ProducerProductScreen: React.FC = (): JSX.Element => {
  const [state, setState] = useState<States>(States.default);
  const [page, setPage] = useState<Page<ProducerProductLink, ProducerProductLink>>({
    page: 1,
    pageSize: 10,
    sort: 'createdAt',
    order: 'ASC',
  });
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(ShouldShowModal.filter);
  const [events, setEvents] = useState<Event[]>([]);
  const [sections, setSections] = useState<Section[]>([]);

  const { title, visible, onChangeTitle, onToggle } = useDialog();
  const navigate = useHistory();

  const handleOnGetProducerProducts = async (
    currentPage: Page<ProducerProductLink, ProducerProductLink>,
  ): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.post<Page<ProducerProductLink, ProducerProductLink>>(
        '/support/producer/product/page',
        currentPage,
      );

      if (data) {
        setPage(data);
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const { formData, formErrors, onChangeFormInput, isFormValid, resetForm } = useForm({
    initialData: {
      eventId: '',
      sectionId: '',
    },
  });

  const handleOnGetSectionsByEvent = async (eventId: string): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<Section[]>(
        `/support/producer/product/event/${eventId}/section`,
      );
      setSections(data);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnClearFilter = async (): Promise<void> => {
    resetForm();
    const newPage = { page: 1, pageSize: 10, sort: 'createdAt', order: 'ASC' } as Page<
      ProducerProductLink,
      ProducerProductLink
    >;
    await handleOnGetProducerProducts(newPage);
    onToggle();
  };

  const handleOnFilter = async (): Promise<void> => {
    if (isFormValid()) {
      const newPage = {
        ...page,
      };
      if (formData[FormInputName.eventId]) {
        if (!newPage.entity) {
          newPage.entity = {} as ProducerProductLink;
        }
        newPage.entity.event = {
          id: formData[FormInputName.eventId],
        } as Event;
        if (formData[FormInputName.sectionId]) {
          if (!newPage.entity) {
            newPage.entity = {} as ProducerProductLink;
          }
          newPage.entity.section = {
            id: formData[FormInputName.sectionId],
          } as Section;
        }
      }
      await handleOnGetProducerProducts(newPage);
      onToggle();
    }
  };

  const handleOnGetEvents = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<Event[]>('/support/producer/product/event');
      setEvents(data);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnShouldShowModal = async (
    value: ShouldShowModal,
    newTitleModal: string | React.ReactNode,
  ): Promise<void> => {
    if (value === ShouldShowModal.filter) {
      await handleOnGetEvents();
    }
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    onToggle();
  };

  const handleOnShowDetail = (id: string): void => {
    navigate.replace(`/support/producer-product/${id}/detail`);
  };

  const handleOnValidate = async (id: string): Promise<void> => {
    try {
      setState(States.loading);
      await api.patch(`/support/producer/product/${id}/validate`);
      toast.success('Produtos validados com sucesso');
      await handleOnGetProducerProducts(page);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnRefused = async (id: string): Promise<void> => {
    try {
      setState(States.loading);
      await api.patch(`/support/producer/product/${id}/refuse`);
      toast.success('Produtos recusados com sucesso');
      await handleOnGetProducerProducts(page);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnPaginationChange = async (selectedPage: number): Promise<void> => {
    const newPage = { ...page, page: selectedPage };
    await handleOnGetProducerProducts(newPage);
  };

  useEffect(() => {
    handleOnGetProducerProducts(page);
  }, []);
  return (
    <ProducerProductContainer
      state={state}
      title={title}
      visible={visible}
      onToggle={onToggle}
      onClearFilter={handleOnClearFilter}
      onFilter={handleOnFilter}
      shouldShowModal={shouldShowModal}
      events={events}
      sections={sections}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
      onGetSectionsByEvent={handleOnGetSectionsByEvent}
      onShouldShowModal={handleOnShouldShowModal}
      currentPage={page}
      onShowDetail={handleOnShowDetail}
      onValidate={handleOnValidate}
      onRefused={handleOnRefused}
      onPaginationChange={handleOnPaginationChange}
    />
  );
};
